import { useState } from 'react'
import {
  Center, Stack, Paper, Title, Text, Select, Textarea, Button, Alert, Group, Loader, ThemeIcon,
} from '@mantine/core'
import { IconBuildingCommunity, IconClock, IconLogout } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { organizationsApi, type OrgStatusResponse } from '@/api/organizations'
import { precisaEscolherOrganizacao } from './precisaEscolherOrganizacao'
import { formatApiError } from '@/api/errors'
import { useAuthStore } from '@/store/auth'
import { useMyOrganizationStatus } from '@/hooks/useOrganizations'

/**
 * Tela do colaborador SEM organização (spec 2026-07-24 §3.1 — fluxo join).
 * - Com pendência: mostra o status + botão cancelar.
 * - Sem pendência: Select das orgs disponíveis + observação + "Solicitar entrada".
 *
 * Renderizada como gate no layout (App.tsx/RequireAuth) — só para não-admin
 * sem organização que o servidor não isentou (`org_required`).
 */
export function NoOrganizationScreen({ status }: { status: OrgStatusResponse }) {
  const qc = useQueryClient()
  const logout = useAuthStore((s) => s.logout)
  const [toOrg, setToOrg] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const pending = status.pending_request

  const invalidate = () => qc.invalidateQueries({ queryKey: ['my-organization-status'] })

  const handleRequest = async () => {
    if (!toOrg) return
    setSubmitting(true)
    try {
      await organizationsApi.requestJoinOrganization({ to_org_id: Number(toOrg), note: note.trim() || undefined })
      invalidate()
      notifications.show({ title: 'Solicitação enviada', message: 'Aguarde a aprovação do gestor da organização', color: 'green' })
    } catch (err) {
      notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao enviar solicitação'), color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!pending) return
    setCancelling(true)
    try {
      await organizationsApi.cancelMembershipRequest(pending.id)
      invalidate()
      notifications.show({ title: 'Cancelada', message: 'Solicitação cancelada', color: 'blue' })
    } catch (err) {
      notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao cancelar solicitação'), color: 'red' })
    } finally {
      setCancelling(false)
    }
  }

  const orgOptions = status.organizations.map(o => ({ value: String(o.id), label: o.name }))

  return (
    <Center style={{ minHeight: '100vh', padding: 24, backgroundColor: 'var(--ai-body-bg)' }}>
      <Paper withBorder shadow="sm" p="xl" radius="md" style={{ maxWidth: 480, width: '100%' }}>
        <Stack gap="md">
          <Group gap="sm">
            <ThemeIcon size={44} radius="md" variant="light" color="teal">
              <IconBuildingCommunity size={26} />
            </ThemeIcon>
            <div>
              <Title order={3}>Você não está em nenhuma Organização</Title>
              <Text size="sm" c="dimmed">Solicite entrada para acessar o ConsoleAI.</Text>
            </div>
          </Group>

          {pending ? (
            <Alert color="blue" variant="light" icon={<IconClock size={16} />}>
              <Text size="sm">
                Sua solicitação está <b>aguardando aprovação de «{pending.to_org_name}»</b>.
              </Text>
              {pending.note && <Text size="xs" c="dimmed" mt={4}>Observação: {pending.note}</Text>}
              <Button
                variant="light"
                color="red"
                size="xs"
                mt="sm"
                loading={cancelling}
                onClick={handleCancel}
              >
                Cancelar solicitação
              </Button>
            </Alert>
          ) : orgOptions.length === 0 ? (
            <Alert color="gray" variant="light">
              Não há organizações disponíveis para solicitar entrada. Contate um administrador.
            </Alert>
          ) : (
            <Stack gap="sm">
              <Select
                label="Organização"
                placeholder="Selecione a organização"
                data={orgOptions}
                value={toOrg}
                onChange={setToOrg}
                searchable
                withAsterisk
              />
              <Textarea
                label="Observação (opcional)"
                placeholder="Apresente-se ou explique o motivo da solicitação"
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                maxLength={500}
                autosize
                minRows={2}
              />
              <Button onClick={handleRequest} loading={submitting} disabled={!toOrg}>
                Solicitar entrada
              </Button>
            </Stack>
          )}

          <Button
            variant="subtle"
            color="gray"
            size="xs"
            leftSection={<IconLogout size={14} />}
            onClick={() => { void logout() }}
          >
            Sair
          </Button>
        </Stack>
      </Paper>
    </Center>
  )
}

/**
 * Gate leve para o layout: busca o status de org e decide se intercepta.
 * Retorna o elemento a renderizar (a tela sem-org) OU null para seguir o fluxo
 * normal. Mantém o acoplamento mínimo — chamado uma única vez no RequireAuth.
 */
export function NoOrganizationGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  // Admin (is_admin OU admin:full) nunca é bloqueado — vive em multi-org.
  const isAdmin = !!user?.is_admin || (user?.global_permissions ?? []).includes('admin:full')

  const { data: status, isLoading } = useMyOrganizationStatus(!isAdmin)

  if (isAdmin) return <>{children}</>
  if (isLoading) {
    return (
      <Center style={{ minHeight: '100vh' }}><Loader size="sm" /></Center>
    )
  }
  // auditor_global vive fora das orgs: o servidor manda org_required=false.
  if (status && precisaEscolherOrganizacao(status)) {
    return <NoOrganizationScreen status={status} />
  }
  return <>{children}</>
}
